<template>
  <b-modal
    ref="modal"
    centered
    hide-header
    hide-footer
    modal-class="customize-folder-modal"
    content-class="customize-folder-modal__content"
    body-class="customize-folder-modal__body p-0"
    @hide="closeHandler"
  >
    <div class="cfm">
      <div class="cfm__header">
        <h2 class="cfm__title">
          Customize folder
        </h2>
        <button
          type="button"
          class="cfm__close"
          aria-label="Close"
          @click="$refs.modal.hide()"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <div class="cfm__preview">
        <div
          class="cfm__preview-box"
          v-html="previewHtml"
        />
      </div>

      <div class="cfm__sections">
        <div class="cfm__section">
          <div class="cfm__label">
            Color
          </div>
          <div class="cfm__colors">
            <button
              type="button"
              class="cfm__swatch cfm__swatch--none"
              :class="{ 'is-selected': !color }"
              aria-label="No color"
              @click="color = null"
            />
            <button
              v-for="swatch in COLORS"
              :key="swatch"
              type="button"
              class="cfm__swatch"
              :class="{ 'is-selected': color === swatch }"
              :aria-label="swatch"
              :style="{ backgroundColor: swatch }"
              @click="color = swatch"
            />
          </div>
        </div>

        <div class="cfm__section">
          <div class="cfm__label">
            Emoji
          </div>
          <div class="cfm__emojis">
            <button
              type="button"
              class="cfm__emoji cfm__emoji--none"
              :class="{ 'is-selected': !emoji }"
              @click="selectEmoji(null)"
            >
              —
            </button>
            <button
              v-for="option in EMOJIS"
              :key="option"
              type="button"
              class="cfm__emoji"
              :class="{ 'is-selected': emoji === option }"
              @click="selectEmoji(option)"
            >
              {{ option }}
            </button>
            <input
              v-model="customEmoji"
              class="cfm__custom"
              type="text"
              placeholder="Custom"
              maxlength="2"
              @input="onCustomEmojiInput"
            >
          </div>
        </div>
      </div>

      <div class="cfm__footer">
        <button
          type="button"
          class="cfm__btn cfm__btn--cancel"
          @click="$refs.modal.hide()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="cfm__btn cfm__btn--save"
          :disabled="saving"
          @click="accept"
        >
          Save
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import modalMixin from './mixin'
import { createNamespacedHelpers } from 'vuex'
import folderSvg from '@frontend/templates/file_image/folder-svg'

const { mapActions } = createNamespacedHelpers('fileBrowser')

const COLORS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#6366f1',
  '#a855f7',
  '#ec4899',
  '#64748b',
  '#0f172a',
]

const EMOJIS = ['📁', '📷', '🎨', '📝', '⭐', '🚀', '💡', '🏷️', '🎬', '🛒', '📊', '🔥']

export default {
  name: 'CustomizeFolderModal',
  mixins: [modalMixin],
  props: {
    payload: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    color: null,
    emoji: null,
    customEmoji: '',
    saving: false,
    COLORS,
    EMOJIS,
  }),
  computed: {
    previewHtml() {
      return folderSvg({
        color: this.color,
        emoji: this.emoji,
        size: 84,
      })
    },
  },
  watch: {
    payload(value) {
      if (!value) {
        return
      }
      this.resetFromPayload(value)
    },
  },
  methods: {
    ...mapActions(['customizeFolder']),
    init() {
      this.resetFromPayload(this.payload || {})
      this.$refs.modal.show()
    },
    resetFromPayload(value) {
      this.color = value.color || null
      this.emoji = value.emoji || null
      const custom = value.emoji && !EMOJIS.includes(value.emoji) ? value.emoji : ''
      this.customEmoji = custom ? Array.from(custom).slice(0, 2).join('') : ''
      if (this.customEmoji) {
        this.emoji = this.customEmoji
      }
      this.saving = false
    },
    selectEmoji(value) {
      this.emoji = value
      this.customEmoji = ''
    },
    onCustomEmojiInput() {
      const chars = Array.from((this.customEmoji || '').trim())
      const value = chars.slice(0, 2).join('')
      this.customEmoji = value
      this.emoji = value || null
    },
    async accept() {
      if (this.saving || !this.payload?.id) {
        return
      }

      this.saving = true
      try {
        await this.customizeFolder({
          id: this.payload.id,
          color: this.color,
          emoji: this.emoji,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
::v-deep .customize-folder-modal .modal-dialog {
  max-width: 520px;
  margin: 1.75rem auto;
}

::v-deep .customize-folder-modal .modal-content {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

::v-deep .customize-folder-modal .modal-body {
  padding: 0 !important;
}

.cfm {
  position: relative;
  padding: 1.5rem;
  background: #fff;
}

.cfm__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 0.25rem;
}

.cfm__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.01em;
  color: #0f172a;
}

.cfm__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 0.125rem;
  background: transparent;
  color: #64748b;
  opacity: 0.7;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
}

.cfm__preview {
  display: flex;
  justify-content: center;
  padding: 0.25rem 0 0.5rem;
}

.cfm__preview-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 7rem; /* w-28 */
  height: 6rem; /* h-24 */
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem; /* rounded-xl */
  background: rgba(241, 245, 249, 0.45); /* bg-muted/30 */
  overflow: hidden;

  ::v-deep .folder-icon-preview {
    width: 84px !important;
    height: 68.88px !important;
    max-width: none;
    max-height: none;

    svg {
      width: 100% !important;
      height: 100% !important;
      max-width: none !important;
      max-height: none !important;
    }
  }
}

.cfm__sections {
  display: flex;
  flex-direction: column;
  gap: 1.5rem; /* space-y-6 */
}

.cfm__label {
  margin-bottom: 0.625rem; /* mb-2.5 */
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em; /* tracking-wider */
  text-transform: uppercase;
  color: #64748b; /* text-muted-foreground */
}

.cfm__colors {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.5rem; /* gap-2 */
}

.cfm__swatch {
  width: 1.75rem; /* h-7 w-7 */
  height: 1.75rem;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background-clip: padding-box;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &.is-selected {
    /* ring-2 ring-foreground ring-offset-2 */
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0f172a;
  }

  &--none {
    background: #fff;
    border: 1px dashed rgba(100, 116, 139, 0.4); /* border-muted-foreground/40 */
  }
}

.cfm__emojis {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem; /* gap-1.5 */
  align-items: center;
}

.cfm__emoji {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem; /* h-9 w-9 */
  height: 2.25rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem; /* rounded-md */
  background: transparent;
  font-size: 1.25rem; /* text-xl */
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: rgba(241, 245, 249, 0.6); /* hover:bg-muted/60 */
  }

  &.is-selected {
    background: #f1f5f9; /* bg-muted */
  }

  &--none {
    font-size: 0.875rem; /* text-sm */
    color: #64748b;
  }
}

.cfm__custom {
  width: 5rem; /* w-20 */
  height: 2.25rem; /* h-9 */
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0; /* border-muted */
  border-radius: 0.375rem;
  background: rgba(241, 245, 249, 0.3); /* bg-muted/30 */
  font-size: 0.875rem;
  line-height: 1.25;
  color: #0f172a;
  outline: none;
  box-shadow: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #cbd5e1;
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(15, 23, 42, 0.15);
  }
}

.cfm__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem; /* sm:space-x-2 */
  padding-top: 0.5rem; /* pt-2 */
}

.cfm__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem; /* h-10 */
  padding: 0.5rem 1rem; /* px-4 py-2 */
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &--cancel {
    background: transparent;
    color: #64748b; /* text-muted-foreground */

    &:hover {
      background: #f1f5f9; /* hover:bg-accent */
      color: #0f172a;
    }
  }

  &--save {
    background: #007964;
    color: #fff;

    &:hover {
      background: #006050;
    }
  }
}
</style>
